/**
 * The plan screen's reads, over T18/T32's `@repo/client/data/plan` options.
 *
 * - `usePlan()`: `GET /me/plan` in the catalogue locale — the same key the
 *   deck (T56) and the workout (T54) read, so they share one cache entry.
 * - `useDraftSummary()`: the draft (`GET /me/plan?draft=1`) and, only while
 *   one exists, what it changes (`GET /me/plan/draft/diff`). The diff's 404
 *   `plan.no_draft` is "no banner", never an error: the draft can be
 *   published or discarded between the two reads.
 *
 * Nothing here writes, and nothing here generates a draft (`D150`, `T-145`):
 * the empty state opens `sheet=draft`, whose review generates.
 */
import { ApiError } from "@repo/client/api";
import {
  activePlanOptions,
  draftDiffOptions,
  draftPlanOptions,
} from "@repo/client/data/plan";
import type { CatalogueLocale } from "@repo/contract/catalog";
import type { DraftDiffOutput } from "@repo/contract/training";
import { useApi } from "../../data/api";
import { useDataQuery } from "../../data/query-client";
import { appI18n } from "../auth/i18n";

/** The app locale as the catalogue's (`pt` / `en`), for localized names. */
export const catalogueLocaleOf = (locale: string): CatalogueLocale =>
  locale.startsWith("pt") ? "pt" : "en";

export function usePlan() {
  const api = useApi();
  const locale = catalogueLocaleOf(appI18n.useLocale());
  return useDataQuery(activePlanOptions(api, { locale }));
}

/** A draft waiting for review, with its diff; `null` when there is none. */
export function useDraftSummary(): DraftDiffOutput | null {
  const api = useApi();
  const locale = catalogueLocaleOf(appI18n.useLocale());
  const draft = useDataQuery(draftPlanOptions(api, { locale }));
  const exists = draft.data !== undefined && draft.data !== null;
  const diff = useDataQuery(draftDiffOptions(api, { locale }), {
    enabled: exists,
  });
  if (!exists || diff.data === undefined) {
    return null;
  }
  // A cached diff that has since gone 404 (published elsewhere) is stale.
  if (diff.error instanceof ApiError && diff.error.status === 404) {
    return null;
  }
  return diff.data;
}
